/**
 * Safely extract a clean, customer-safe user message from any error value.
 * Prevents raw HTML, [object Object], stack traces, or internal secrets from leaking to the UI.
 */
export function getErrorMessage(
  error: unknown,
  fallback = "An unexpected error occurred."
): string {
  if (!error) return fallback;

  if (typeof error === "string") {
    const trimmed = error.trim();
    if (trimmed.startsWith("<") || trimmed.toLowerCase().includes("<html>") || trimmed.toLowerCase().includes("403 forbidden")) {
      return fallback;
    }
    return trimmed;
  }

  if (error instanceof Error && error.message) {
    const msg = error.message.trim();
    if (msg.startsWith("<") || msg.toLowerCase().includes("<html>") || msg.toLowerCase().includes("403 forbidden")) {
      return fallback;
    }
    return msg;
  }

  if (typeof error === "object") {
    const errObj = error as Record<string, any>;

    if (typeof errObj.userMessage === "string" && errObj.userMessage.trim()) {
      return errObj.userMessage.trim();
    }

    if (typeof errObj.message === "string" && errObj.message.trim()) {
      const msg = errObj.message.trim();
      if (!msg.startsWith("<") && !msg.toLowerCase().includes("<html>") && !msg.toLowerCase().includes("403 forbidden")) {
        return msg;
      }
    }

    if (typeof errObj.error === "string" && errObj.error.trim()) {
      const err = errObj.error.trim();
      if (!err.startsWith("<") && !err.toLowerCase().includes("<html>") && !err.toLowerCase().includes("403 forbidden")) {
        return err;
      }
    }

    if (errObj.error && typeof errObj.error === "object") {
      return getErrorMessage(errObj.error, fallback);
    }

    if (errObj.details && typeof errObj.details === "string" && !errObj.details.startsWith("<")) {
      return errObj.details;
    }
  }

  return fallback;
}
